import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import "../chatbot-v2.css";
// Import UI components from lift-ui
import { 
  Button, 
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  SingleSelect,
  PageHeader,
  SpinnerLoader,
} from "lift-ui";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAdminData } from "../hooks/useAdminData";
import {
  FeedbackTypeV2,
  getSessionMemoryJson,
  BrandDataV2Response,
  AdminSessionV2FilterPayload,
  getSessionData,
  getUserMemories,
  UserMemory,
  updateMessageMetadata,
  getAnalyticsData,
  getUsersOrderedBySessions,
  getDistinctBrandIds,
  getAvailableModels,
  getSessionFeedbackStats,
  GroupByV2,
  createSession,
  getAgentSessions,
  updateMessageFeedback,
  getApiRoute,
  getDistinctChannels,
  DistinctChannelsResponse,
  WorkflowRunDetailResponse,
} from "../api";
import { FilterPanel, SessionsPanel, AnalyticsPanel, WorkflowRunsPanel, MasterMemoriesPanel } from "./admin";
import { Message, MessageContent, MessageAvatar } from "./prompt-kit/message";
// Icons from lucide-react
import {
  RefreshCw,
  Filter,
  MessageSquare,
  Users,
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  AlertCircle,
  X,
  Search,
  TrendingUp,
  Activity,
  Code,
  Clock,
  Settings,
  Zap,
  Target,
  Building,
  Hash,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Calendar,
  PieChart,
  ArrowRight,
  List,
  FileText,
  Download,
  ExternalLink,
  Loader2,
  Brain,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, subMonths, addMonths } from "date-fns";
import { useChatV2 } from "../context/ChatV2Context";
import { prepareReportHtml } from "../lib/reportHtml";
import apiClient from "@lift-admin/utils/apiClient";

// Add the same scrollbar styles from CommandHistory
const ADMIN_SCROLLBAR_STYLES = `
  .admin-scroll {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    scrollbar-gutter: stable;
    overflow: overlay;
  }
  
  .admin-scroll::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    position: absolute;
  }
  
  .admin-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .admin-scroll::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 4px;
  }
  
  .admin-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.8);
  }
  
  .admin-container:hover .admin-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    transition: none;
  }
  
  .admin-container:not(:hover) .admin-scroll::-webkit-scrollbar-thumb {
    background-color: transparent;
    transition: background-color 0.5s ease;
  }
  
  .admin-container:hover .admin-scroll {
    scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
    transition: none;
  }
  
  .admin-container:not(:hover) .admin-scroll {
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.5s ease;
  }

  /* Horizontal scroll handling for content within chat messages */
  .admin-scroll .prose table {
    font-size: 0.75rem;
    line-height: 1rem;
    max-width: 100%;
    table-layout: auto;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  
  .admin-scroll .prose th,
  .admin-scroll .prose td {
    word-break: break-word;
    overflow-wrap: anywhere;
    max-width: 0;
    padding: 0.25rem 0.5rem;
  }
`;

// Inject the styles into the document
if (
  typeof window !== "undefined" &&
  !document.getElementById("admin-scrollbar-styles")
) {
  const styleEl = document.createElement("style");
  styleEl.id = "admin-scrollbar-styles";
  styleEl.innerHTML = ADMIN_SCROLLBAR_STYLES;
  document.head.appendChild(styleEl);
}

// Chat message interface matching CommandHistory
interface ChatMessage {
  role: string;
  content: string;
  hasCode?: boolean;
  isTruncated?: boolean;
  rawContent?: string;
  timestamp?: string;
  run_id?: string; // Add run_id for feedback matching
  feedback?: {
    is_liked?: boolean;
    comment?: string;
    reasons?: string[]; // Add reasons array
    type?: 'liked' | 'disliked' | 'no_feedback' | 'unknown'; // Add feedback type
  };
  htmlSnippets?: Array<{ id: string; html: string; isComplete: boolean }>;
  events?: any[]; // Add events to support tool-based HTML detection
}

// Function to get fresh initial filters - fetch all sessions for client-side pagination
const getInitialFilters = (): AdminSessionV2FilterPayload => {
  return {
    page_size: 10000, // Large number to fetch all sessions
    // Note: exclude_workflow_tests is handled client-side in SessionsPanel
  };
};

// Stable reference for initial filters (will be recalculated on component mount)
const STABLE_INITIAL_FILTERS: AdminSessionV2FilterPayload = {
  page_size: 10000, // Large number to fetch all sessions
  // Note: exclude_workflow_tests is handled client-side in SessionsPanel
};

// Helper function to convert cron expression to human-readable format
const cronToHumanReadable = (cron: string): string => {
  if (!cron) return '';
  
  const parts = cron.split(' ').filter(p => p.trim());
  if (parts.length < 5) return cron;
  
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  
  if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every minute';
  }
  
  if (minute === '0' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every hour';
  }
  
  const hourIntervalMatch = hour.match(/^\*\/(\d+)$/);
  if (minute === '0' && hourIntervalMatch && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return `Every ${hourIntervalMatch[1]} hours`;
  }
  
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `Daily at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth === '*' && month === '*' && dayOfWeek.match(/^[0-6](,[0-6])*$/)) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayIndices = dayOfWeek.split(',').map(d => parseInt(d));
    const dayNames = dayIndices.map(d => days[d]).join(', ');
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `Every ${dayNames} at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth.match(/^\d+$/) && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour);
    const m = parseInt(minute);
    const d = parseInt(dayOfMonth);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const suffix = d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
    return `Monthly on the ${d}${suffix} at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  return `Cron: ${cron}`;
};

// Helper to parse workflow prompt content
interface ParsedWorkflowPrompt {
  userPrompt: string;
  hasThreadHistory: boolean;
  threadHistory: Array<{ query: string; answer: string; index: number }>;
  mainQuery?: string;
  additionalPrompt?: string;
}

const parseWorkflowPrompt = (content: string): ParsedWorkflowPrompt => {
  const result: ParsedWorkflowPrompt = {
    userPrompt: content,
    hasThreadHistory: false,
    threadHistory: [],
  };

  // Helper function to parse JSON formatted_history
  const parseFormattedHistory = (jsonStr: string): boolean => {
    try {
      const parsed = JSON.parse(jsonStr.trim());
      
      if (parsed.formatted_history && Array.isArray(parsed.formatted_history)) {
        result.hasThreadHistory = true;
        
        // The formatted_history array has separate objects for queries and answers
        // Structure: [{query1: "..."}, {answer1: "..."}, {query2: "..."}, {answer2: "..."}, ..., {main_query: "..."}]
        const queryMap: Record<number, string> = {};
        const answerMap: Record<number, string> = {};
        let mainQueryFromArray = '';
        
        parsed.formatted_history.forEach((item: any) => {
          if (!item || typeof item !== 'object') return;
          
          // Check each key in the item
          Object.keys(item).forEach((key) => {
            const queryMatch = key.match(/^query(\d+)$/);
            const answerMatch = key.match(/^answer(\d+)$/);
            
            if (queryMatch) {
              const idx = parseInt(queryMatch[1], 10);
              queryMap[idx] = item[key];
            } else if (answerMatch) {
              const idx = parseInt(answerMatch[1], 10);
              answerMap[idx] = item[key];
            } else if (key === 'main_query') {
              mainQueryFromArray = item[key];
            }
          });
        });
        
        // Get main_query from either the array or the root object
        result.mainQuery = mainQueryFromArray || parsed.main_query || '';
        result.additionalPrompt = parsed.additional_prompt || '';
        result.userPrompt = parsed.additional_prompt || result.mainQuery || '';
        
        // Now pair up queries and answers
        const indices = [...new Set([...Object.keys(queryMap), ...Object.keys(answerMap)])]
          .map(Number)
          .sort((a, b) => a - b);
        
        indices.forEach((idx) => {
          if (queryMap[idx] !== undefined || answerMap[idx] !== undefined) {
            result.threadHistory.push({
              query: queryMap[idx] || '',
              answer: answerMap[idx] || '',
              index: idx,
            });
          }
        });
        
        // Only mark as having thread history if we actually found pairs
        if (result.threadHistory.length > 0) {
          result.hasThreadHistory = true;
        }
        
        return true;
      }
    } catch (e) {
      // Not valid JSON
    }
    return false;
  };

  // First, try to extract from XML-like format <user_prompt>...</user_prompt>
  const userPromptMatch = content.match(/<user_prompt>\s*([\s\S]*?)\s*<\/user_prompt>/);
  if (userPromptMatch) {
    const extractedContent = userPromptMatch[1].trim();
    
    // Try to parse the extracted content as JSON
    if (extractedContent.startsWith('{')) {
      if (parseFormattedHistory(extractedContent)) {
        return result;
      }
    }
    
    // If not JSON or parsing failed, just use the extracted content
    result.userPrompt = extractedContent;
    return result;
  }

  // If no XML wrapper, try to parse the content directly as JSON
  const trimmed = content.trim();
  if (trimmed.startsWith('{')) {
    if (parseFormattedHistory(trimmed)) {
      return result;
    }
  }

  return result;
};

// Collapsible Thread History Component (for header section)
const CollapsibleThreadHistory = ({ 
  history 
}: { 
  history: Array<{ query: string; answer: string; index: number }> 
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [openItems, setOpenItems] = React.useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newSet = new Set(openItems);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setOpenItems(newSet);
  };

  if (history.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-slate-600" />
          <span className="text-sm font-semibold text-slate-700">Thread History</span>
          <Badge variant="secondary" className="text-xs bg-slate-200 text-slate-700">
            {history.length} exchanges
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">
            {isExpanded ? 'Collapse' : 'Expand'}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {history.map((item) => (
            <div key={item.index} className="border border-slate-300 rounded-lg overflow-hidden shadow-sm">
              <button
                onClick={() => toggleItem(item.index)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 border border-blue-200 flex-shrink-0">
                    #{item.index}
                  </Badge>
                  <span className="text-sm text-slate-800 truncate">
                    {item.query.substring(0, 60)}{item.query.length > 60 ? '...' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  <span className="text-xs text-slate-500">
                    {openItems.has(item.index) ? 'Hide' : 'Show'}
                  </span>
                  {openItems.has(item.index) ? (
                    <ChevronUp className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                </div>
              </button>
              {openItems.has(item.index) && (
                <div className="p-3 space-y-3 bg-white border-t border-slate-200">
                  <div>
                    <div className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      User Query:
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap bg-blue-50 p-3 rounded-lg border border-blue-200">
                      {item.query}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      AI Response:
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap bg-green-50 p-3 rounded-lg border border-green-200 max-h-64 overflow-y-auto">
                      {item.answer}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ Lifted out at module scope (stable type)
// Custom Calendar Component for Unified Filters - matching lift-ui styling
const UnifiedFilterCalendar: React.FC<{
  selected?: Date;
  onSelect: (date: Date) => void;
}> = ({ selected, onSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(selected || new Date());
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const startDay = monthStart.getDay();
  const paddingDays = Array(startDay).fill(null);
  
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <div className="p-3 bg-white rounded-xl">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="p-1 hover:bg-[#F4FFC6] rounded-full transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-[#585755]" />
        </button>
        <span className="text-sm font-medium text-[#1A1A1A]">
          {format(currentMonth, 'MMMM yyyy')}
        </span>
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="p-1 hover:bg-[#F4FFC6] rounded-full transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-[#585755]" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 mb-2">
        {weekDays.map((day) => (
          <div key={day} className="text-center text-xs font-normal text-[#3D3D3D] py-1">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {paddingDays.map((_, index) => (
          <div key={`pad-${index}`} className="w-9 h-9" />
        ))}
        {days.map((day) => {
          const isSelected = selected && isSameDay(day, selected);
          const isToday = isSameDay(day, new Date());
          
          return (
            <button
              key={day.toISOString()}
              onClick={() => onSelect(day)}
              className={cn(
                "w-9 h-9 flex items-center justify-center text-sm font-normal rounded-full transition-colors",
                isSelected 
                  ? "bg-[#334238] text-white" 
                  : isToday 
                    ? "border border-[#1A1A1A] text-[#1A1A1A]"
                    : "text-[#1A1A1A] hover:bg-[#F4FFC6]"
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Custom DatePicker for Unified Filters - matching lift-ui styling
const UnifiedFilterDatePicker: React.FC<{
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = "Select date" }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "w-full h-10 px-3 py-2 text-left text-sm rounded-lg border border-input bg-white",
            "hover:border-[#585755] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "flex items-center justify-between transition-colors",
            !value && "text-muted-foreground"
          )}
        >
          <span>{value ? format(value, 'MMM dd, yyyy') : placeholder}</span>
          <Calendar className="w-4 h-4 text-[#585755]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border shadow-lg rounded-xl" align="start">
        <UnifiedFilterCalendar
          selected={value}
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const UnifiedFilterPanel: React.FC<{
  draftFilters: AdminSessionV2FilterPayload;
  setDraftFilters: React.Dispatch<
    React.SetStateAction<AdminSessionV2FilterPayload>
  >;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  tagInputRef: React.RefObject<HTMLInputElement | null>;
  minInputRef: React.RefObject<HTMLInputElement | null>;
  maxInputRef: React.RefObject<HTMLInputElement | null>;
  applyFilters: () => void;
  resetAllFilters: () => void;
  showPanelFiltersPopover: boolean;
  setShowPanelFiltersPopover: React.Dispatch<React.SetStateAction<boolean>>;
  unifiedFilters: AdminSessionV2FilterPayload;
  activeFilterCount: number;
  setShowUnifiedFilters: React.Dispatch<React.SetStateAction<boolean>>;
  users: string[];
  loading: { users: boolean; brands: boolean; models: boolean; channels?: boolean };
  brands: any;
  models: string[];
  userMap: Record<string, string>;
  brandMap: any;
  channels?: { channel_id: string; channel_name: string; session_count: number }[];
}> = ({
  draftFilters,
  setDraftFilters,
  searchInputRef,
  tagInputRef,
  minInputRef,
  maxInputRef,
  applyFilters,
  resetAllFilters,
  showPanelFiltersPopover,
  setShowPanelFiltersPopover,
  unifiedFilters,
  activeFilterCount,
  setShowUnifiedFilters,
  users,
  loading,
  brands,
  models,
  userMap,
  brandMap,
  channels,
}) => {
  return (
    <Card className="w-full max-h-full overflow-hidden flex flex-col border-0 shadow-none" shadow="soft">
      <div className="pb-3 pt-4 px-4 bg-white border-b border-[#E5E1DF] flex-shrink-0 flex flex-row items-center justify-between gap-4">
        <div className="text-base font-semibold flex items-center gap-2 text-[#242424]">
          <Filter className="h-5 w-5 text-[#334238]" />
          Unified Filters
          {activeFilterCount > 0 && (
            <div
              className="relative inline-block ml-2"
              onMouseEnter={() => setShowPanelFiltersPopover(true)}
              onMouseLeave={() => setShowPanelFiltersPopover(false)}
            >
              <Badge
                variant="electricGreen"
                className="cursor-pointer"
              >
                {activeFilterCount} active
              </Badge>
                {showPanelFiltersPopover && (
                  <Card className="absolute top-full left-0 mt-1 w-64 bg-white border border-[#E5E1DF] rounded-xl shadow-lg z-50">
                    <CardHeader className="py-2 px-3 bg-[#F4FFC6] border-b border-[#E5E1DF] rounded-t-xl">
                      <CardTitle className="text-sm font-medium text-[#334238]">
                        Active Filters
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 space-y-1 text-sm text-[#242424]">
                      <ul className="space-y-1">
                        {unifiedFilters.search_term && (
                          <li>
                            <strong>Search:</strong>{" "}
                            {unifiedFilters.search_term}
                          </li>
                        )}
                        {unifiedFilters.tag_search && (
                          <li>
                            <strong>Tag:</strong> {unifiedFilters.tag_search}
                          </li>
                        )}
                        {unifiedFilters.start_date && (
                          <li>
                            <strong>Start Date:</strong>{" "}
                            {format(unifiedFilters.start_date, "MMM dd, yyyy")}
                          </li>
                        )}
                        {unifiedFilters.end_date && (
                          <li>
                            <strong>End Date:</strong>{" "}
                            {format(unifiedFilters.end_date, "MMM dd, yyyy")}
                          </li>
                        )}
                        {unifiedFilters.feedback_type &&
                          unifiedFilters.feedback_type !==
                            FeedbackTypeV2.ALL && (
                            <li>
                              <strong>Feedback:</strong>{" "}
                              {unifiedFilters.feedback_type}
                            </li>
                          )}
                        {unifiedFilters.user_id && (
                          <li>
                            <strong>User:</strong>{" "}
                            {userMap?.[unifiedFilters.user_id] ||
                              unifiedFilters.user_id}
                          </li>
                        )}
                        {unifiedFilters.brand_id && (
                          <li>
                            <strong>Brand:</strong>{" "}
                            {brandMap?.[parseInt(unifiedFilters.brand_id)] ||
                              unifiedFilters.brand_id}
                          </li>
                        )}
                        {unifiedFilters.channel_id && (
                          <li>
                            <strong>Channel:</strong>{" "}
                            {channels?.find(c => c.channel_id === unifiedFilters.channel_id)?.channel_name ||
                              unifiedFilters.channel_id}
                          </li>
                        )}
                        {unifiedFilters.model && (
                          <li>
                            <strong>Model:</strong> {unifiedFilters.model}
                          </li>
                        )}
                        {unifiedFilters.min_chat_length != null && (
                          <li>
                            <strong>Min Messages:</strong>{" "}
                            {unifiedFilters.min_chat_length}
                          </li>
                        )}
                        {unifiedFilters.max_chat_length != null && (
                          <li>
                            <strong>Max Messages:</strong>{" "}
                            {unifiedFilters.max_chat_length}
                          </li>
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={resetAllFilters}
            className="gap-1.5"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={applyFilters}
            className="gap-1.5"
          >
            Apply Filters
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowUnifiedFilters(false)}
            className="h-9 w-9 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <CardContent className="p-4 overflow-y-auto flex-1 min-h-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-4 w-full">
          {/* Start Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <Calendar className="h-4 w-4 text-[#585755]" />
              Start Date
            </label>
            <UnifiedFilterDatePicker
              value={draftFilters.start_date ? new Date(draftFilters.start_date) : undefined}
              onChange={(date) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  start_date: date,
                }))
              }
              placeholder="Select start date"
            />
          </div>
          
          {/* End Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <Calendar className="h-4 w-4 text-[#585755]" />
              End Date
            </label>
            <UnifiedFilterDatePicker
              value={draftFilters.end_date ? new Date(draftFilters.end_date) : undefined}
              onChange={(date) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  end_date: date,
                }))
              }
              placeholder="Select end date"
            />
          </div>
          
          {/* Feedback Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <MessageSquare className="h-4 w-4 text-[#585755]" />
              Feedback Type
            </label>
            <SingleSelect
              options={[
                { value: FeedbackTypeV2.ALL, label: "All Feedback" },
                { value: FeedbackTypeV2.LIKED, label: "Liked Only" },
                { value: FeedbackTypeV2.DISLIKED, label: "Disliked Only" },
                { value: FeedbackTypeV2.NONE, label: "No Feedback" },
              ]}
              defaultValue={draftFilters.feedback_type || FeedbackTypeV2.ALL}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  feedback_type: value as FeedbackTypeV2,
                }))
              }
              placeholder="All Feedback"
              className="w-full"
              selectClassName="h-10"
            />
          </div>
          
          {/* User Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <Users className="h-4 w-4 text-[#585755]" />
              User
            </label>
            <SingleSelect
              options={[
                { value: "all", label: "All Users" },
                ...users.map((user) => ({
                  value: user,
                  label: userMap?.[user] || user,
                })),
              ]}
              defaultValue={draftFilters.user_id || "all"}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  user_id: value === "all" ? undefined : value,
                }))
              }
              placeholder="All Users"
              className="w-full"
              selectClassName="h-10"
            />
          </div>
          
          {/* Brand Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <Building className="h-4 w-4 text-[#585755]" />
              Brand
            </label>
            <SingleSelect
              options={[
                { value: "all", label: "All Brands" },
                ...(brands?.data?.map((brand: BrandDataV2Response) => ({
                  value: brand.brand_id.toString(),
                  label: `${brandMap?.[brand.brand_id] || `Brand ${brand.brand_id}`} (${brand.session_count})`,
                })) || []),
              ]}
              defaultValue={draftFilters.brand_id || "all"}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  brand_id: value === "all" ? undefined : value,
                }))
              }
              placeholder="All Brands"
              className="w-full"
              selectClassName="h-10"
            />
          </div>
          
          {/* Model Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <Zap className="h-4 w-4 text-[#585755]" />
              Model
            </label>
            <SingleSelect
              options={[
                { value: "all", label: "All Models" },
                ...models.map((model: string) => ({
                  value: model,
                  label: model,
                })),
              ]}
              defaultValue={draftFilters.model || "all"}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  model: value === "all" ? undefined : value,
                }))
              }
              placeholder="All Models"
              className="w-full"
              selectClassName="h-10"
            />
          </div>
          
          {/* Channel Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2 text-[#242424]">
              <Hash className="h-4 w-4 text-[#585755]" />
              Channel
            </label>
            <SingleSelect
              options={[
                { value: "all", label: "All Channels" },
                ...(channels?.map((channel) => ({
                  value: channel.channel_id,
                  label: `${channel.channel_name} (${channel.session_count})`,
                })) || []),
              ]}
              defaultValue={draftFilters.channel_id || "all"}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  channel_id: value === "all" ? undefined : value,
                }))
              }
              placeholder="All Channels"
              className="w-full"
              selectClassName="h-10"
            />
          </div>
          
          {/* Min Messages */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#242424]">
              Min Messages
            </label>
            <Input
              type="number"
              placeholder="Min messages"
              ref={minInputRef}
              defaultValue={draftFilters.min_chat_length?.toString() || ""}
              className="h-10 bg-white border-input hover:border-[#585755] rounded-lg"
            />
          </div>
          
          {/* Max Messages */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#242424]">
              Max Messages
            </label>
            <Input
              type="number"
              placeholder="Max messages"
              ref={maxInputRef}
              defaultValue={draftFilters.max_chat_length?.toString() || ""}
              className="h-10 bg-white border-input hover:border-[#585755] rounded-lg"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard: React.FC = () => {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [showUnifiedFilters, setShowUnifiedFilters] = useState(false);
  const [showActiveFiltersPopover, setShowActiveFiltersPopover] =
    useState(false);
  const [showPanelFiltersPopover, setShowPanelFiltersPopover] = useState(false);
  const [filterTab, setFilterTab] = useState<
    "search" | "feedback" | "advanced"
  >("search");
  const [chatMessages, setChatMessages] = useState<ChatMessage[] | null>(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const [panelRatio, setPanelRatio] = useState(0.4);
  const [isResizing, setIsResizing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState("overview");
  
  // Debug: Log when state changes
  useEffect(() => {
    console.log('[AdminDashboard] activeAnalyticsTab state changed to:', activeAnalyticsTab);
  }, [activeAnalyticsTab]);
  
  // Debug: Log when activeAnalyticsTab state changes
  useEffect(() => {
    console.log('[AdminDashboard] activeAnalyticsTab state changed to:', activeAnalyticsTab);
  }, [activeAnalyticsTab]);
  const [showHtmlDialog, setShowHtmlDialog] = useState(false);
  const [selectedHtml, setSelectedHtml] = useState<string>("");

  // User Memories Dialog State
  const [showUserMemoriesDialog, setShowUserMemoriesDialog] = useState(false);
  const [userMemories, setUserMemories] = useState<UserMemory[]>([]);
  const [loadingUserMemories, setLoadingUserMemories] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Workflow Run Detail State
  const [selectedWorkflowRun, setSelectedWorkflowRun] = useState<WorkflowRunDetailResponse | null>(null);
  const [workflowChatMessages, setWorkflowChatMessages] = useState<ChatMessage[] | null>(null);
  const [loadingWorkflowChat, setLoadingWorkflowChat] = useState(false);

  // Trend period state
  const [trendsPeriod, setTrendsPeriod] = useState("last_30d");

  // Fixed unified filter state - using fresh initial filters with current date range
  const [unifiedFilters, setUnifiedFilters] =
    useState<AdminSessionV2FilterPayload>(() => getInitialFilters());

  // Draft filters for editing in the Unified Filter Panel
  const [draftFilters, setDraftFilters] =
    useState<AdminSessionV2FilterPayload>(() => getInitialFilters());
  
  // Optimized filter synchronization - only sync when panel opens, not on every unifiedFilters change
  useEffect(() => {
    if (showUnifiedFilters) {
      setDraftFilters(unifiedFilters);
    }
  }, [showUnifiedFilters]); // Removed unifiedFilters dependency

  // Sync input field values with draftFilters changes (for reset and other state updates)
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.value = draftFilters.search_term || "";
    }
    if (tagInputRef.current) {
      tagInputRef.current.value = draftFilters.tag_search || "";
    }
    if (minInputRef.current) {
      minInputRef.current.value =
        draftFilters.min_chat_length?.toString() || "";
    }
    if (maxInputRef.current) {
      maxInputRef.current.value =
        draftFilters.max_chat_length?.toString() || "";
    }
  }, [
    draftFilters.search_term,
    draftFilters.tag_search,
    draftFilters.min_chat_length,
    draftFilters.max_chat_length,
  ]);

  // Refs for filter inputs to allow unrestricted typing
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const lastClientX = useRef<number>(0);

  const {
    sessions,
    analytics,
    timeSeriesAnalytics,
    trendsAnalytics,
    detailedAnalytics,
    users,
    brands,
    models,
    loading,
    errors,
    filters,
    updateFilters,
    isLoading,
    hasError,
    clearErrors,
    fetchSessions,
    fetchAnalytics,
    fetchTimeSeriesAnalytics,
    fetchTrendsAnalytics,
    fetchDetailedAnalytics,
    fetchUsers,
    fetchBrands,
    fetchModels,
  } = useAdminData({
    autoFetch: false,
    initialFilters: getInitialFilters(),
  });

  const { brandMap, userMap } = useChatV2();

  // Channels state for filtering
  const [channels, setChannels] = useState<{ channel_id: string; channel_name: string; session_count: number }[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // Fetch channels for filter dropdown
  const fetchChannels = useCallback(async () => {
    setLoadingChannels(true);
    try {
      const response = await getDistinctChannels();
      if (response.success) {
        setChannels(response.data);
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  // Function to detect HTML content in tool results (from teams MessageAssistant)
  const extractHtmlSnippets = useCallback(
    (
      events: any[],
      toolCalls?: any[],
      responseTools?: any[] // Add new parameter for run.response.tools
    ): Array<{ id: string; html: string; isComplete: boolean }> => {
      const extractedSnippets: Array<{
        id: string;
        html: string;
        isComplete: boolean;
      }> = [];

      // NEW: Extract from run.response.tools[] where tool_name is "atransfer_task_to_member"
      if (responseTools && Array.isArray(responseTools)) {
        responseTools.forEach((tool, toolIndex) => {
          if (tool.tool_name === "atransfer_task_to_member" && tool.content) {
            // Check if content contains HTML
            const hasHtml =
              tool.content.includes("<html") ||
              tool.content.includes("<!DOCTYPE html") ||
              (tool.content.includes("<") &&
                tool.content.includes("</") &&
                (tool.content.includes("<div") ||
                  tool.content.includes("<table") ||
                  tool.content.includes("<body")));

            if (hasHtml) {
              console.log(
                `[HTML Debug] Found HTML content in atransfer_task_to_member tool at index ${toolIndex}`
              );
              extractedSnippets.push({
                id: `response-tool-${tool.created_at || Date.now()}-${toolIndex}`,
                html: tool.content,
                isComplete: true,
              });
            }
          }
        });
      }

      // Extract from events first (if provided) - keep existing logic
      if (events && events.length > 0) {
        const completedEvents = events.filter(
          (e) => e.event === "ToolCallCompleted"
        );

        completedEvents.forEach((event, eventIndex) => {
          if ("tools" in event && event.tools) {
            event.tools.forEach((tool: any, toolIndex: number) => {
              if (tool.content && typeof tool.content === "string") {
                // First check for direct HTML content
                const hasDirectHtml =
                  tool.content.includes("<html") ||
                  tool.content.includes("<!DOCTYPE html") ||
                  (tool.content.includes("<") &&
                    tool.content.includes("</") &&
                    (tool.content.includes("<div") ||
                      tool.content.includes("<table")));

                if (hasDirectHtml) {
                  console.log(
                    "[HTML Debug] Found direct HTML content in tool result"
                  );
                  extractedSnippets.push({
                    id: `tool-${event.created_at}-${toolIndex}`,
                    html: tool.content,
                    isComplete: true,
                  });
                  return;
                }

                // Try to parse as JSON and extract HTML
                try {
                  const parsedContent = JSON.parse(tool.content);
                  if (
                    parsedContent.html &&
                    typeof parsedContent.html === "string"
                  ) {
                    console.log(
                      "[HTML Debug] Found HTML in parsed JSON content"
                    );
                    extractedSnippets.push({
                      id: `tool-${event.created_at}-${toolIndex}-json`,
                      html: parsedContent.html,
                      isComplete: true,
                    });
                  } else if (
                    parsedContent.content &&
                    typeof parsedContent.content === "string" &&
                    (parsedContent.content.includes("<html") ||
                      parsedContent.content.includes("<!DOCTYPE html"))
                  ) {
                    console.log(
                      "[HTML Debug] Found HTML in content field of parsed JSON"
                    );
                    extractedSnippets.push({
                      id: `tool-${event.created_at}-${toolIndex}-content`,
                      html: parsedContent.content,
                      isComplete: true,
                    });
                  }
                } catch (e) {
                  console.error("Error parsing tool content as JSON:", e);
                }
              }
            });
          }
        });
      }

      // Check tool calls directly if provided - keep existing logic
      if (toolCalls && Array.isArray(toolCalls)) {
        toolCalls.forEach((toolCall, index) => {
          if (toolCall.function && toolCall.function.arguments) {
            try {
              const args =
                typeof toolCall.function.arguments === "string"
                  ? JSON.parse(toolCall.function.arguments)
                  : toolCall.function.arguments;

              // Check for HTML in expected_output or other argument fields
              if (
                args.expected_output &&
                typeof args.expected_output === "string" &&
                (args.expected_output.includes("<html") ||
                  args.expected_output.includes("<!DOCTYPE html"))
              ) {
                console.log("[HTML Debug] Found HTML in expected_output!");
                extractedSnippets.push({
                  id: `toolcall-${index}-expected`,
                  html: args.expected_output,
                  isComplete: true,
                });
              }

              // Check all string arguments for HTML content
              Object.entries(args).forEach(([key, value]) => {
                if (
                  typeof value === "string" &&
                  (value.includes("<html") || value.includes("<!DOCTYPE html"))
                ) {
                  console.log(`[HTML Debug] Found HTML in argument ${key}!`);
                  extractedSnippets.push({
                    id: `toolcall-${index}-${key}`,
                    html: value,
                    isComplete: true,
                  });
                }
              });
            } catch (e) {
              console.log(`[HTML Debug] Failed to parse arguments:`, e);
            }
          }
        });
      }

      return extractedSnippets;
    },
    []
  );

  // Function to format message content (updated to match teams implementation)
  const formatMessageContent = (
    content: string,
    role?: string,
    toolCalls?: any[],
    events?: any[],
    responseTools?: any[] // Add new parameter for run.response.tools
  ) => {
    console.log(`[HTML Debug] formatMessageContent called:`, {
      content: content?.substring(0, 100),
      role,
      toolCalls: toolCalls?.length || 0,
      events: events?.length || 0,
      responseTools: responseTools?.length || 0,
    });

    if (!content) {
      console.log(`[HTML Debug] No content provided`);
      return {
        formatted: "",
        hasCode: false,
        isTruncated: false,
        rawContent: "",
        htmlSnippets: [],
      };
    }

    // For admin dashboard, don't truncate content - show full messages
    const maxLength = 10000; // Increased from 1000 to 10000
    const hasCode = content.includes("```");
    const isTruncated = content.length > maxLength;

    // Detect HTML snippets using the teams implementation logic
    let htmlSnippets: Array<{ id: string; html: string; isComplete: boolean }> =
      [];

    // Check for direct HTML content in message content
    if (
      content.includes("<html") ||
      content.includes("<!DOCTYPE html") ||
      content.includes("```html")
    ) {
      console.log(`[HTML Debug] Found HTML markers in content`);

      // Extract HTML from markdown code blocks
      const htmlBlockMatch = content.match(/```html\s+([\s\S]*?)```/);
      if (htmlBlockMatch) {
        console.log(`[HTML Debug] Extracted HTML from markdown block`);
        htmlSnippets.push({
          id: `content-html-${Date.now()}`,
          html: htmlBlockMatch[1].trim(),
          isComplete: true,
        });
      }
      // Direct HTML content
      else if (
        content.includes("<html") ||
        content.includes("<!DOCTYPE html")
      ) {
        console.log(`[HTML Debug] Using direct HTML content`);
        htmlSnippets.push({
          id: `content-direct-${Date.now()}`,
          html: content,
          isComplete: true,
        });
      }
    }

    // Extract HTML from tool calls, events, and response tools
    const toolHtmlSnippets = extractHtmlSnippets(
      events || [],
      toolCalls,
      responseTools
    );
    htmlSnippets = [...htmlSnippets, ...toolHtmlSnippets];

    // Also check for JSON responses that might contain HTML
    try {
      const parsed = JSON.parse(content);
      console.log(`[HTML Debug] Checking parsed JSON:`, parsed);
      if (parsed.html && typeof parsed.html === "string") {
        console.log(`[HTML Debug] Found HTML in JSON html field!`);
        htmlSnippets.push({
          id: `json-html-${Date.now()}`,
          html: parsed.html,
          isComplete: true,
        });
      } else if (
        parsed.content &&
        typeof parsed.content === "string" &&
        (parsed.content.includes("<html") ||
          parsed.content.includes("<!DOCTYPE html"))
      ) {
        console.log(`[HTML Debug] Found HTML in JSON content field!`);
        htmlSnippets.push({
          id: `json-content-${Date.now()}`,
          html: parsed.content,
          isComplete: true,
        });
      }
    } catch {
      // Not JSON, continue with existing logic
    }

    const result = {
      formatted: isTruncated
        ? content.substring(0, maxLength) + "..."
        : content,
      hasCode,
      isTruncated,
      rawContent: content,
      htmlSnippets,
    };

    console.log(`[HTML Debug] Final result:`, {
      htmlSnippetsCount: htmlSnippets.length,
      snippetIds: htmlSnippets.map((s) => s.id),
    });

    return result;
  };

  // Fetch chat preview using the existing getSessionMemoryJson API with direct message feedback lookup
  const fetchChatPreview = useCallback(
    async (sessionId: string): Promise<ChatMessage[]> => {
      try {
        console.log(
          `[AdminDashboard] Fetching chat data for session: ${sessionId}`
        );

        const sessionData = await getSessionMemoryJson(sessionId);
        console.log(`[AdminDashboard] Session data:`, sessionData);

        const chatMessages: ChatMessage[] = [];

        if (sessionData?.runs && Array.isArray(sessionData.runs)) {
          console.log(
            `[AdminDashboard] Processing ${sessionData.runs.length} runs`
          );

          // First, fetch all feedback data for this session in one call
          let feedbackMap: Record<string, any> = {};
          try {
            console.log(`[AdminDashboard] Fetching feedback data for session: ${sessionId}`);
            const feedbackResponse = await apiClient.get(`${getApiRoute("/liftai/messages")}/${sessionId}`);
            if (feedbackResponse.status === 200 && Array.isArray(feedbackResponse.data)) {
              // Convert array of {run_id: {feedback_data}} objects to a flat map
              feedbackResponse.data.forEach((item: any) => {
                const runId = Object.keys(item)[0];
                const feedbackData = item[runId];
                if (runId && feedbackData) {
                  feedbackMap[runId] = feedbackData;
                }
              });
              console.log(`[AdminDashboard] Retrieved feedback for ${Object.keys(feedbackMap).length} messages`);
            }
          } catch (error) {
            console.warn('[AdminDashboard] Failed to fetch session feedback data:', error);
            // Continue without feedback data
          }

          // Process all runs and apply feedback data
          sessionData.runs.forEach((run: any) => {
            console.log("run", run);

            // Extract user message (from message object)
            if (run.message && run.message.content) {
              const formattedContent = formatMessageContent(
                run.message.content,
                run.message.role,
                run.message.tool_calls,
                run.message.events,
                run.message.tools
              );

              chatMessages.push({
                role: "user",
                content: formattedContent.formatted,
                hasCode: formattedContent.hasCode,
                isTruncated: formattedContent.isTruncated,
                rawContent: formattedContent.rawContent,
                timestamp: run.message.timestamp || run.created_at,
                htmlSnippets: formattedContent.htmlSnippets,
                events: run.message.events,
              });
            }

            // Extract assistant message (from response object) and apply feedback
            if (run.response && run.response.content) {
              const formattedContent = formatMessageContent(
                run.response.content,
                run.response.role,
                run.response.tool_calls,
                run.response.events,
                run.response.tools
              );

              // Get feedback data for this run_id
              const feedbackData = feedbackMap[run.response.run_id];
              let feedback: any = {
                ...run.response.feedback, // Keep any existing feedback from the response
                type: 'no_feedback' // Default
              };

              if (feedbackData) {
                const isLiked = feedbackData.is_liked;
                feedback = {
                  is_liked: isLiked === 1 ? true : isLiked === 0 ? false : undefined,
                  comment: feedbackData.user_response?.comment,
                  reasons: feedbackData.user_response?.reasons,
                  type: isLiked === 1 ? 'liked' : isLiked === 0 ? 'disliked' : 'no_feedback'
                };
              }

              chatMessages.push({
                role: "assistant",
                content: formattedContent.formatted,
                hasCode: formattedContent.hasCode,
                isTruncated: formattedContent.isTruncated,
                rawContent: formattedContent.rawContent,
                timestamp: run.response.timestamp || run.updated_at,
                run_id: run.response.run_id, // Add run_id for feedback tracking
                feedback: feedback,
                htmlSnippets: formattedContent.htmlSnippets,
                events: run.response.events,
              });
            }

            // Also check for tool calls at the run level
            if (run.tool_calls && Array.isArray(run.tool_calls)) {
              run.tool_calls.forEach((toolCall: any, index: number) => {
                if (
                  toolCall.content ||
                  (toolCall.function && toolCall.function.arguments)
                ) {
                  const toolContent =
                    toolCall.content ||
                    (typeof toolCall.function?.arguments === "string"
                      ? toolCall.function.arguments
                      : JSON.stringify(toolCall.function?.arguments || {}));

                  const formattedContent = formatMessageContent(
                    toolContent,
                    "tool",
                    [toolCall],
                    toolCall.events,
                    toolCall.tools
                  );

                  if (formattedContent.htmlSnippets.length > 0) {
                    chatMessages.push({
                      role: "tool",
                      content: formattedContent.formatted,
                      hasCode: formattedContent.hasCode,
                      isTruncated: formattedContent.isTruncated,
                      rawContent: formattedContent.rawContent,
                      timestamp: toolCall.created_at || run.updated_at,
                      htmlSnippets: formattedContent.htmlSnippets,
                    });
                  }
                }
              });
            }
          });
        }

        // If no messages from runs, try the top-level messages array as fallback
        if (
          chatMessages.length === 0 &&
          sessionData.messages &&
          Array.isArray(sessionData.messages)
        ) {
          console.log(
            `[AdminDashboard] Using ${sessionData.messages.length} messages from top-level messages array`
          );

          sessionData.messages.forEach((msg: any) => {
            if (
              msg.role &&
              (msg.role === "user" ||
                msg.role === "assistant" ||
                msg.role === "tool") &&
              msg.content
            ) {
              const formattedContent = formatMessageContent(
                msg.content,
                msg.role,
                msg.tool_calls,
                msg.events,
                msg.tools
              );

              chatMessages.push({
                role: msg.role,
                content: formattedContent.formatted,
                hasCode: formattedContent.hasCode,
                isTruncated: formattedContent.isTruncated,
                rawContent: formattedContent.rawContent,
                timestamp: msg.timestamp || msg.created_at,
                feedback: msg.feedback,
                htmlSnippets: formattedContent.htmlSnippets,
              });
            }
          });
        }

        // Sort messages by their logical order
        chatMessages.sort((a, b) => {
          if (a.timestamp && b.timestamp) {
            return (
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
          }
          return 0;
        });

        console.log(
          `[AdminDashboard] Extracted ${chatMessages.length} messages for preview`
        );

        // Debug: Log messages with HTML
        const htmlMessages = chatMessages.filter(
          (msg) => msg.htmlSnippets && msg.htmlSnippets.length > 0
        );
        console.log(
          `[HTML Debug] Found ${htmlMessages.length} messages with HTML:`,
          htmlMessages
        );

        return chatMessages;
      } catch (error) {
        console.error(`[AdminDashboard] Error fetching chat preview:`, error);
        throw error;
      }
    },
    []
  );

  const handleSessionSelect = async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setLoadingChat(true);
    try {
      // Fetch chat messages - the session memory will contain run_ids
      const messages = await fetchChatPreview(sessionId);
      setChatMessages(messages);
    } catch (error) {
      console.error("Failed to load chat messages:", error);
      setChatMessages([]);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleBackToSessions = () => {
    setSelectedSessionId(null);
    setChatMessages(null);
  };

  // Workflow run selection handler - fetches chat messages for the workflow run's session
  const handleWorkflowRunSelect = async (run: WorkflowRunDetailResponse) => {
    setSelectedWorkflowRun(run);
    setLoadingWorkflowChat(true);
    try {
      // Fetch chat messages using the session_id from the workflow run
      const messages = await fetchChatPreview(run.session_id);
      setWorkflowChatMessages(messages);
    } catch (error) {
      console.error("Failed to load workflow run chat messages:", error);
      setWorkflowChatMessages([]);
    } finally {
      setLoadingWorkflowChat(false);
    }
  };

  const handleBackToWorkflowRuns = () => {
    setSelectedWorkflowRun(null);
    setWorkflowChatMessages(null);
  };

  // Fetch latest sessions data whenever the user switches to the "sessions" tab
  const hasSessionsDataRef = useRef(false);
  useEffect(() => {
    if (activeTab === "sessions" && !hasSessionsDataRef.current) {
      // Use individual fetch functions instead of refreshAll to avoid dependency issues
      Promise.allSettled([
        fetchSessions(),
        fetchAnalytics(),
        fetchUsers(),
        fetchBrands(),
        fetchModels(),
        fetchChannels(),
      ]).catch(console.error);
      hasSessionsDataRef.current = true;
    }
  }, [activeTab, fetchSessions, fetchAnalytics, fetchUsers, fetchBrands, fetchModels, fetchChannels]); // Remove refreshAll dependency

  // Fetch overview data when AdminDashboard first loads or when switching to overview tab
  const hasOverviewDataRef = useRef(false);
  useEffect(() => {
    if (activeTab === "overview" && !hasOverviewDataRef.current) {
      // Fetch all data needed for overview analytics
      Promise.allSettled([
        fetchAnalytics(),
        fetchTimeSeriesAnalytics(),
        fetchTrendsAnalytics(),
        fetchDetailedAnalytics(),
        fetchUsers(),
        fetchBrands(),
        fetchModels(),
      ]).catch(console.error);
      hasOverviewDataRef.current = true;
    }
  }, [activeTab, fetchAnalytics, fetchTimeSeriesAnalytics, fetchTrendsAnalytics, fetchDetailedAnalytics, fetchUsers, fetchBrands, fetchModels]);

  const handleRefresh = async () => {
    clearErrors();
    
    // Clear unified filters on refresh
    const baseReset: AdminSessionV2FilterPayload = {
      page_size: 10000, // Large number to fetch all sessions
      // Note: exclude_workflow_tests is handled client-side in SessionsPanel
    };
    setUnifiedFilters(baseReset);
    setDraftFilters(baseReset);
    updateFilters(baseReset);
    lastAppliedFilters.current = baseReset;
    
    // Only refresh data relevant to the current tab to avoid unnecessary API calls
    // This prevents the global state refresh issue where changing any filter would reload everything
    if (activeTab === "sessions") {
      // For sessions tab, only refresh sessions data with cleared filters
      await fetchSessions(baseReset);
    } else if (activeTab === "overview") {
      // For overview tab, refresh analytics and overview data
      await Promise.allSettled([
        fetchAnalytics(),
        fetchTimeSeriesAnalytics(),
        fetchTrendsAnalytics(),
        fetchDetailedAnalytics(),
      ]);
    }
  };

  // Enhanced metrics using detailed analytics overview and aggregated analytics data
  const enhancedMetrics = React.useMemo(() => {
    const overview = detailedAnalytics?.overview ?? {
      total_sessions: 0,
      total_users: 0,
      sessions_with_feedback: 0,
      avg_session_length: 0,
      total_messages: 0,
    };
    const totalSessions = overview.total_sessions || 0;
    const totalUsers = overview.total_users || 0;
    const totalMessages = overview.total_messages || 0;
    const avgSessionLength = overview.avg_session_length || 0;
    const sessionsWithFeedback = overview.sessions_with_feedback || 0;
    const totalBrands = brands?.total_distinct_brands || 0;
    const satisfactionRate =
      totalSessions > 0 ? (sessionsWithFeedback / totalSessions) * 100 : 0;
    const likedMessages = analytics?.total_liked_messages || 0;
    const dislikedMessages = analytics?.total_disliked_messages || 0;
    const noFeedbackMessages = analytics?.total_no_feedback_messages || 0;

    return {
      totalSessions,
      totalUsers,
      totalMessages,
      avgSessionLength,
      totalBrands,
      satisfactionRate,
      likedMessages,
      dislikedMessages,
      noFeedbackMessages,
    };
  }, [detailedAnalytics, analytics, brands]);

  // Active filter badge should reflect *applied* filters (those stored in unifiedFilters),
  // not what the user is currently typing in the local draft fields.
  const activeFilterCount = useMemo(() => {
    let count = 0;

    // Applied search filters
    if (unifiedFilters.search_term) count++;
    if (unifiedFilters.tag_search) count++;

    // Other applied filters
    Object.entries(unifiedFilters).forEach(([key, value]) => {
      if (
        key === "page" ||
        key === "page_size" ||
        key === "search_term" ||
        key === "tag_search"
      )
        return;
      if (key === "feedback_type" && value === FeedbackTypeV2.ALL) return;
      if (value !== null && value !== undefined && value !== "") count++;
    });

    return count;
  }, [unifiedFilters]);

  // Handle trend period change
  const handleTrendsPeriodChange = useCallback((period: string) => {
    setTrendsPeriod(period);
    // Fetch new trends data with the selected period
    fetchTrendsAnalytics(period);
  }, [fetchTrendsAnalytics]);

  // Handle time series period change
  const handleTimeSeriesPeriodChange = useCallback((period: string, granularity?: string) => {
    // Fetch new time series data with the selected period, defaulting to day if not specified
    fetchTimeSeriesAnalytics({ time_range: period, granularity: granularity || 'day' });
  }, [fetchTimeSeriesAnalytics]);

  // Handle detailed analytics period change (for overview metrics)
  // This also covers user engagement since it's part of detailed analytics
  const handleDetailedAnalyticsPeriodChange = useCallback((period: string) => {
    // Fetch new detailed analytics data with the selected period
    fetchDetailedAnalytics({ time_range: period });
  }, [fetchDetailedAnalytics]);

  // User engagement period change handler - returns same promise as detailed analytics
  // to avoid duplicate API calls (user engagement data comes from detailed analytics endpoint)
  const handleUserEngagementPeriodChange = useCallback((period: string) => {
    // Return undefined since detailed analytics already handles this
    // The AnalyticsPanel calls both, so we only need one to actually fetch
    return undefined;
  }, []);

  // Enhanced KPI Card component with new size options - MOVED BEFORE EnhancedKPISection
  const KPICard = ({
    title,
    value,
    icon: Icon,
    className,
    trend,
    trendDirection,
    size = "normal",
  }: {
    title: string;
    value: number | string;
    icon: React.ElementType;
    className?: string;
    trend?: string;
    trendDirection?: "up" | "down";
    size?: "normal" | "small" | "compact" | "mini";
  }) => {
    const sizeClasses = {
      normal: "p-3",
      small: "p-2",
      compact: "p-1.5",
      mini: "p-1",
    };

    const textSizes = {
      normal: { title: "text-xs", value: "text-lg", trend: "text-xs" },
      small: { title: "text-xs", value: "text-base", trend: "text-xs" },
      compact: {
        title: "text-xs",
        value: "text-sm font-bold",
        trend: "text-xs",
      },
      mini: { title: "text-xs", value: "text-sm font-bold", trend: "text-xs" },
    };

    const iconSizes = {
      normal: "h-4 w-4",
      small: "h-3 w-3",
      compact: "h-3 w-3",
      mini: "h-3 w-3",
    };

    return (
      <Card className={cn("transition-all hover:shadow-sm border", className)}>
        <CardContent
          className={cn("flex items-center justify-between", sizeClasses[size])}
        >
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                "text-muted-foreground font-medium leading-tight",
                textSizes[size].title
              )}
            >
              {title}
            </p>
            <p className={cn("leading-tight mt-0.5", textSizes[size].value)}>
              {typeof value === "number" ? value.toLocaleString() : value}
            </p>
            {trend && size !== "mini" && (
              <div
                className={cn(
                  "flex items-center gap-1 mt-0.5",
                  textSizes[size].trend,
                  trendDirection === "up" ? "text-emerald-600" : "text-rose-600"
                )}
              >
                <TrendingUp className="h-2.5 w-2.5" />
                <span>{trend}</span>
              </div>
            )}
          </div>
          <Icon
            className={cn(
              "text-muted-foreground/70 flex-shrink-0",
              iconSizes[size]
            )}
          />
        </CardContent>
      </Card>
    );
  };

  // Enhanced KPI component with embedded analytics - reduced spacing
  const EnhancedKPISection = useMemo(() => (
    <div className="space-y-4 w-full">
      {/* Increased from space-y-1.5 */}
      {/* Primary KPIs Row - More compact */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 w-full">
        {/* {" "} */}
        {/* Slightly increased gap */}
        {/* <KPICard
          title="Sessions"
          value={enhancedMetrics.totalSessions}
          icon={MessageSquare}
          size="compact"
        />
        <KPICard
          title="Active Users"
          value={enhancedMetrics.totalUsers}
          icon={Users}
          size="compact"
        />
        <KPICard
          title="Messages"
          value={enhancedMetrics.totalMessages}
          icon={MessageSquare}
          size="compact"
        />
        <KPICard
          title="Avg Session Length"
          value={enhancedMetrics.avgSessionLength.toFixed(1)}
          icon={Clock}
          size="compact"
        /> */}
        {/* <KPICard
          title="Like Ratio"
          value={`${enhancedMetrics.likedMessages}/${enhancedMetrics.totalMessages}`}
          icon={ThumbsUp}
          className="border-emerald-200 bg-emerald-50"
          size="compact"
        /> */}
        {/* <KPICard
          title="Active Brands"
          value={enhancedMetrics.totalBrands}
          icon={Building}
          size="compact"
        /> */}
      </div>
      {/* Comprehensive Analytics Dashboard - Full Size */}
      <div className="bg-background border border-border rounded-lg p-4 w-full">
        {/* Increased padding and removed height restriction */}
        <AnalyticsPanel
          analytics={analytics}
          sessions={sessions}
          brands={brands}
          loading={loading.analytics}
          compact={false}
          timeSeriesAnalytics={timeSeriesAnalytics}
          trendsAnalytics={trendsAnalytics}
          detailedAnalytics={detailedAnalytics}
          onTrendsPeriodChange={handleTrendsPeriodChange}
          onTimeSeriesPeriodChange={handleTimeSeriesPeriodChange}
          onDetailedAnalyticsPeriodChange={handleDetailedAnalyticsPeriodChange}
          activeAnalyticsTab={activeAnalyticsTab}
          onAnalyticsTabChange={setActiveAnalyticsTab}
          onUserEngagementPeriodChange={handleUserEngagementPeriodChange}
        />
      </div>
    </div>
  ), [enhancedMetrics, analytics, sessions, brands, loading.analytics, timeSeriesAnalytics, trendsAnalytics, detailedAnalytics, handleTrendsPeriodChange, handleTimeSeriesPeriodChange, handleDetailedAnalyticsPeriodChange, handleUserEngagementPeriodChange, activeAnalyticsTab, setActiveAnalyticsTab]);

  // Reference to track the last applied filters (used for dirty-state detection if needed)
  const lastAppliedFilters =
    useRef<AdminSessionV2FilterPayload>(getInitialFilters());

  // Helper that applies the currently edited filters
  const applyFilters = useCallback(() => {
    const newFilters: AdminSessionV2FilterPayload = { ...draftFilters };
    const searchVal = searchInputRef.current?.value;
    newFilters.search_term =
      searchVal && searchVal.trim() !== "" ? searchVal.trim() : undefined;
    const tagVal = tagInputRef.current?.value;
    newFilters.tag_search =
      tagVal && tagVal.trim() !== "" ? tagVal.trim() : undefined;
    const minVal = minInputRef.current?.value;
    newFilters.min_chat_length = minVal ? parseInt(minVal) : undefined;
    const maxVal = maxInputRef.current?.value;
    newFilters.max_chat_length = maxVal ? parseInt(maxVal) : undefined;
    
    // Reset to page 1 when applying new filters
    newFilters.page = 1;
    
    setUnifiedFilters(newFilters);
    updateFilters(newFilters);
    // Call fetchSessions to refresh session panel
    fetchSessions(newFilters);
    lastAppliedFilters.current = newFilters;
    
    // Close the filter panel after applying
    setShowUnifiedFilters(false);
  }, [draftFilters, updateFilters, setShowUnifiedFilters, fetchSessions]);

  // Unified filter update handler - fixed to prevent state bugs
  const handleUnifiedFilterChange = useCallback(
    (key: keyof AdminSessionV2FilterPayload, value: any) => {
      setDraftFilters((prev) => ({
        ...prev,
        [key]: value,
        page: key !== "page" ? 1 : prev.page, // Reset to page 1 when changing filters except page
      }));
    },
    []
  );

  // Reset all filters
  const resetAllFilters = useCallback(() => {
    // Base filters we want after reset
    const baseReset: AdminSessionV2FilterPayload = {
      page_size: 10000, // Large number to fetch all sessions
      feedback_type: FeedbackTypeV2.ALL,
      // Note: exclude_workflow_tests is handled client-side in SessionsPanel
    };

    // Build a full object that explicitly sets every previously‐known key to undefined if not in baseReset
    const clearedFilters: AdminSessionV2FilterPayload = { ...baseReset } as any;
    Object.keys(unifiedFilters).forEach((key) => {
      if (!(key in baseReset)) {
        (clearedFilters as any)[key] = undefined;
      }
    });

    setUnifiedFilters(clearedFilters);
    setDraftFilters(clearedFilters);
    updateFilters(clearedFilters);
    lastAppliedFilters.current = clearedFilters;
  }, [unifiedFilters, updateFilters]);

  // HTML Dialog View Component
  const HTMLDialogView = React.memo(
    ({ html, onClose }: { html: string; onClose: () => void }) => {
      const iframeRef = useRef<HTMLIFrameElement>(null);
      const [isExporting, setIsExporting] = useState(false);

      useEffect(() => {
        if (iframeRef.current && html) {
          // `prepareReportHtml` strips markdown fences, rewrites Lift
          // logo URLs, and injects the chart bootstrap (multi-CDN
          // fallback for ApexCharts/Chart.js/ECharts + tab-switch
          // re-render so charts in hidden tab panels actually paint).
          iframeRef.current.srcdoc = prepareReportHtml(html);
        }
      }, [html]);

      const exportAsPNG = async () => {
        if (!iframeRef.current) return;

        try {
          setIsExporting(true);
          // Simple download logic - in production, you'd use html2canvas like in teams
          const blob = new Blob([html], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `chat-report-${new Date().toISOString().slice(0, 10)}.html`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error exporting HTML:", error);
        } finally {
          setIsExporting(false);
        }
      };

      return (
        <div className="w-full h-full flex flex-col relative z-[10000] bg-white rounded-lg overflow-hidden shadow-2xl">
          {/* Enhanced Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">LIFT AI Report Viewer</h3>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={exportAsPNG}
                disabled={isExporting}
                size="sm"
                variant="secondary"
                className="gap-2 bg-white/20 hover:bg-white/30 text-white border-white/30 hover:border-white/50"
              >
                <Download className="h-4 w-4" />
                {isExporting ? "Exporting..." : "Export HTML"}
              </Button>
              <Button
                onClick={onClose}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Iframe Container with Better Styling */}
          <div className="flex-1 overflow-hidden bg-gray-50 relative p-2">
            {/* Loading indicator */}
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="flex items-center gap-2 text-gray-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading report...</span>
              </div>
            </div>

            <iframe
              ref={iframeRef}
              className="w-full h-full border-0 bg-white relative z-20"
              title="HTML Report Preview"
              sandbox="allow-scripts allow-popups allow-same-origin allow-forms"
              loading="lazy"
              onLoad={() => {
                // Hide loading indicator when iframe loads
                const loadingDiv = document.querySelector(
                  ".absolute.inset-0.flex.items-center.justify-center.bg-white.z-10"
                );
                if (loadingDiv) {
                  (loadingDiv as HTMLElement).style.display = "none";
                }
              }}
            />
          </div>

          {/* Footer with additional info */}
          <div className="px-4 py-2 bg-gray-100 border-t text-xs text-gray-600 flex items-center justify-between">
            <span>Generated report</span>
            <span>Powered by LIFT AI</span>
          </div>
        </div>
      );
    }
  );

  // HtmlVisualization component (from teams implementation)
  const HtmlVisualization = React.memo(
    ({
      htmlSnippets,
    }: {
      htmlSnippets: Array<{ id: string; html: string; isComplete: boolean }>;
    }) => {
      // Return immediately for empty snippets
      if (!htmlSnippets || htmlSnippets.length === 0) {
        return null;
      }

      // Check if all snippets are complete and at least one has HTML containing valid tags
      const hasHtmlContent = htmlSnippets.some(
        (snippet) =>
          snippet.isComplete &&
          (snippet.html.includes("<html") ||
            snippet.html.includes("<!DOCTYPE html") ||
            (snippet.html.includes("<") &&
              snippet.html.includes("</") &&
              (snippet.html.includes("<div") ||
                snippet.html.includes("<table") ||
                snippet.html.includes("<body"))))
      );

      // Always make the report available as long as we have any HTML content
      const shouldShowReportButton = hasHtmlContent;

      if (!shouldShowReportButton) {
        return null;
      }

      const handleOpenReport = () => {
        const targetSnippet =
          htmlSnippets.find((s) => s.isComplete) || htmlSnippets[0];
        if (targetSnippet) {
          handleHtmlVisualization(targetSnippet.html);
        }
      };

      return (
        <div className="mt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleOpenReport}
            className="gap-1 h-7 px-2 text-xs bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
          >
            <FileText className="h-3 w-3" />
            View HTML Report
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      );
    }
  );

  // Function to handle HTML visualization
  const handleHtmlVisualization = (htmlContent: string) => {
    setSelectedHtml(htmlContent);
    setShowHtmlDialog(true);
  };

  // Resizable panel logic (adapted from ChatBotV2Box.tsx)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!containerRef.current) return;

    setIsResizing(true);
    lastClientX.current = e.clientX;
    document.body.classList.add("admin-resizing");

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const newX = e.clientX;
      const containerRect = containerRef.current.getBoundingClientRect();
      const relativeX = newX - containerRect.left;

      // Calculate new ratio (limit between 30% and 80%)
      const newRatio = Math.max(0.3, Math.min(0.8, relativeX / containerWidth));
      setPanelRatio(newRatio);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.classList.remove("admin-resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // Handle tab change - close chat history when switching away from sessions
  const handleTabChange = (newTab: string) => {
    if (activeTab === "sessions" && newTab !== "sessions" && selectedSessionId) {
      setSelectedSessionId(null);
      setChatMessages(null);
    }
    setActiveTab(newTab);
  };

  // Get selected session details for the chat header
  const selectedSessionDetails = useMemo(() => {
    if (!selectedSessionId || !sessions?.data) return null;
    return sessions.data.find((session: any) => session.session_id === selectedSessionId);
  }, [selectedSessionId, sessions?.data]);

  // Add state for session metadata
  const [sessionMetadata, setSessionMetadata] = useState<any>(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);

  // Fetch session metadata when a session is selected
  useEffect(() => {
    const fetchSessionMetadata = async () => {
      if (!selectedSessionId) {
        setSessionMetadata(null);
        return;
      }

      setLoadingMetadata(true);
      try {
        const sessionData = await getSessionData(selectedSessionId);
        console.log(`[AdminDashboard] Session metadata:`, sessionData);
        
        // Parse metadata if it's a string
        let metadata = sessionData?.metadata || {};
        if (typeof metadata === 'string') {
          try {
            metadata = JSON.parse(metadata);
          } catch (e) {
            console.error('Error parsing session metadata:', e);
            metadata = {};
          }
        }
        
        setSessionMetadata(metadata);
      } catch (error) {
        console.error('Error fetching session metadata:', error);
        setSessionMetadata(null);
      } finally {
        setLoadingMetadata(false);
      }
    };

    fetchSessionMetadata();
  }, [selectedSessionId]);

  // Helper function to format brand names from brandMap
  const formatBrandNames = (brandMap: any) => {
    if (!brandMap || typeof brandMap !== 'object') return null;
    
    const brandIds = Object.keys(brandMap);
    if (brandIds.length === 0) return null;
    
    const brandNames = brandIds.map(brandId => {
      const brandName = brandMap[brandId];
      return brandName || `Brand ${brandId}`;
    });
    
    return brandNames.join(', ');
  };

  // Function to fetch and display user memories
  const handleViewUserMemories = async (userId: string) => {
    setSelectedUserId(userId);
    setLoadingUserMemories(true);
    setShowUserMemoriesDialog(true);
    
    try {
      console.log(`[AdminDashboard] Fetching user memories for user: ${userId}`);
      const memories = await getUserMemories(userId);
      setUserMemories(memories);
      console.log(`[AdminDashboard] Retrieved ${memories.length} memories for user ${userId}`);
    } catch (error) {
      console.error('Failed to load user memories:', error);
      setUserMemories([]);
    } finally {
      setLoadingUserMemories(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="users flex h-full w-full admin-container bg-white overflow-hidden"
    >
      {/* Global Loading Overlay */}
      {(loading.users || loading.brands || loading.models) && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 text-white">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div
        className="flex flex-col h-full bg-white overflow-hidden"
        style={{
          width: selectedSessionId ? `${panelRatio * 100}%` : "100%",
          transition: isResizing ? "none" : "width 0.2s ease",
        }}
      >
        {/* Header with Controls - Using PageHeader like Drawer Structure */}
        <PageHeader
          className="flex-shrink-0"
          title="Chatbot Admin Dashboard"
          renderCustomTop={
            <div className="flex items-center gap-4 w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={loading.analytics}
                className="flex items-center gap-2 h-8 px-3"
              >
                {loading.analytics ? (
                  <SpinnerLoader className="h-4 w-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </Button>
            </div>
          }
        />

        {/* Tab Navigation - Lift UI Style */}
        <div className="px-8 pt-4 pb-4 flex-shrink-0">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-fit">
              <TabsTrigger value="overview">Analytics Dashboard</TabsTrigger>
              <TabsTrigger value="sessions">Session Manager</TabsTrigger>
              <TabsTrigger value="workflows">Workflow Runs</TabsTrigger>
              <TabsTrigger value="master-memories">Master Memories</TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Analytics Sub-Toggle - Only show when Analytics Dashboard is active */}
          {activeTab === "overview" && (
            <div className="mt-4">
              <Tabs 
                value={activeAnalyticsTab || "overview"} 
                onValueChange={(value) => {
                  console.log('[AdminDashboard] Tabs onValueChange called with:', value, 'current state:', activeAnalyticsTab);
                  setActiveAnalyticsTab(value);
                  console.log('[AdminDashboard] State set to:', value);
                }}
                className="w-auto"
              >
                <TabsList>
                  <TabsTrigger value="overview">Usage & Trends</TabsTrigger>
                  <TabsTrigger value="models">Models & Brands</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
        </div>

        {/* Error Display */}
        {hasError && (
          <div className="flex-shrink-0 border-b bg-destructive/5">
            <div className="p-3">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-destructive">
                    Error Loading Data
                  </h4>
                  <div className="text-sm text-destructive/80 truncate">
                    {Object.entries(errors)
                      .filter(([, error]) => error)
                      .map(([key, error]) => `${key}: ${error}`)
                      .join(", ")}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearErrors}
                    className="mt-2 h-6 px-2 text-xs"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unified Filters Panel */}
        {showUnifiedFilters && (
          <div className="flex-shrink-0 border-b bg-muted/30 max-h-[60vh] overflow-y-auto admin-scroll">
            <div className="p-2">
              <UnifiedFilterPanel
                draftFilters={draftFilters}
                setDraftFilters={setDraftFilters}
                searchInputRef={searchInputRef}
                tagInputRef={tagInputRef}
                minInputRef={minInputRef}
                maxInputRef={maxInputRef}
                applyFilters={applyFilters}
                resetAllFilters={resetAllFilters}
                showPanelFiltersPopover={showPanelFiltersPopover}
                setShowPanelFiltersPopover={setShowPanelFiltersPopover}
                unifiedFilters={unifiedFilters}
                activeFilterCount={activeFilterCount}
                setShowUnifiedFilters={setShowUnifiedFilters}
                users={users}
                loading={{ ...loading, channels: loadingChannels }}
                brands={brands}
                models={models}
                userMap={userMap}
                brandMap={brandMap}
                channels={channels}
              />
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-hidden bg-white flex flex-col">
          <Tabs value={activeTab} className="flex-1 min-h-0 flex flex-col">
            <TabsContent
              value="overview"
              className="flex-1 min-h-0 overflow-auto admin-scroll m-0 p-3 w-full"
            >
              {EnhancedKPISection}
            </TabsContent>

            <TabsContent
              value="sessions"
              className="flex-1 min-h-0 overflow-hidden m-0 flex flex-col"
            >
              <SessionsPanel
                sessions={sessions}
                loading={loading.sessions}
                filters={filters}
                onFiltersChange={(newFilters) => {
                  console.log(`[AdminDashboard] onFiltersChange called with:`, newFilters);
                  console.log(`[AdminDashboard] Current filters from useAdminData:`, filters);
                  
                  // Check if this is ONLY a page change (pagination)
                  const filterKeys = Object.keys(newFilters);
                  const isPageOnlyChange = filterKeys.length === 1 && filterKeys[0] === 'page';
                  const isPageSizeChange = filterKeys.length === 2 && filterKeys.includes('page') && filterKeys.includes('page_size');
                  
                  if (isPageOnlyChange || isPageSizeChange) {
                    // For pagination changes, merge with existing filters
                    const mergedFilters = { ...filters, ...newFilters };
                    console.log(`[AdminDashboard] Pagination change - merged filters:`, mergedFilters);
                    updateFilters(mergedFilters);
                    handleBackToSessions();
                    fetchSessions(mergedFilters);
                  } else {
                    // For other filter changes, use the original logic
                    console.log(`[AdminDashboard] Non-pagination change - using newFilters directly:`, newFilters);
                    updateFilters(newFilters);
                    handleBackToSessions();
                    fetchSessions(newFilters);
                  }
                }}
                onSessionSelect={handleSessionSelect}
                selectedSessionId={selectedSessionId}
              />
            </TabsContent>

            <TabsContent
              value="workflows"
              className="flex-1 min-h-0 overflow-hidden m-0"
            >
              <div className="h-full flex">
                {/* Workflow Runs List */}
                <div 
                  className="flex flex-col min-h-0"
                  style={{
                    width: selectedWorkflowRun ? `${panelRatio * 100}%` : "100%",
                    transition: isResizing ? "none" : "width 0.2s ease",
                  }}
                >
                  <WorkflowRunsPanel onRunSelect={handleWorkflowRunSelect} selectedRun={selectedWorkflowRun} />
                </div>

                {/* Resizable Divider for Workflow Runs */}
                {selectedWorkflowRun && (
                  <div
                    className="w-4 cursor-col-resize flex items-center justify-center bg-border hover:bg-gray-300 transition-all duration-150 group relative"
                    onMouseDown={handleMouseDown}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 h-20">
                      <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors" />
                      <div className="w-1 h-3 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors" />
                      <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors" />
                    </div>
                    <div className="absolute inset-0 bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-150 rounded-sm" />
                  </div>
                )}

                {/* Workflow Run Detail Panel */}
                {selectedWorkflowRun && (
                  <div
                    className="flex flex-col bg-background border-l min-h-0"
                    style={{
                      width: `${(1 - panelRatio) * 100}%`,
                      transition: isResizing ? "none" : "width 0.2s ease",
                    }}
                  >
                    {/* Workflow Run Header */}
                    <div className="border-b bg-card shadow-sm flex-shrink-0 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4 text-purple-600" />
                          <h2 className="text-sm font-semibold">Workflow Run Details</h2>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBackToWorkflowRuns}
                          className="h-7 px-2"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {/* Workflow Run Metadata */}
                      <div className="mt-2 space-y-2">
                        {/* Workflow Name */}
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-foreground max-w-[550px] whitespace-normal break-words">
                            {selectedWorkflowRun.workflow_name || "Unnamed Workflow"}
                          </div>
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "h-5 text-xs",
                              selectedWorkflowRun.trigger_type === 'manual' 
                                ? "bg-orange-100 text-orange-800 border-orange-300"
                                : "bg-blue-100 text-blue-800 border-blue-300"
                            )}
                          >
                            {selectedWorkflowRun.trigger_type || 'scheduled'}
                          </Badge>
                          {selectedWorkflowRun.workflow_agent && (
                            <Badge variant="secondary" className="h-5 text-xs bg-purple-100 text-purple-800 border-purple-300">
                              {selectedWorkflowRun.workflow_agent}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Workflow Metadata Row */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                          {selectedWorkflowRun.workflow_created_by && (
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              <span className="font-medium">Created by:</span>
                              <span>
                                {userMap?.[selectedWorkflowRun.workflow_created_by.toString()] || `User ${selectedWorkflowRun.workflow_created_by}`}
                              </span>
                            </div>
                          )}
                          {selectedWorkflowRun.workflow_schedule && (
                            <div className="flex items-center gap-1" title={`Cron: ${selectedWorkflowRun.workflow_schedule}`}>
                              <Clock className="h-3 w-3" />
                              <span className="font-medium">Schedule:</span>
                              <span>{cronToHumanReadable(selectedWorkflowRun.workflow_schedule)}</span>
                            </div>
                          )}
                          {selectedWorkflowRun.workflow_session_created_at && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span className="font-medium">Run at:</span>
                              <span>
                                {format(new Date(selectedWorkflowRun.workflow_session_created_at), "MMM dd, yyyy HH:mm")}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Workflow Prompt Preview - Parsed */}
                        {selectedWorkflowRun.workflow_prompt && (() => {
                          const parsed = parseWorkflowPrompt(selectedWorkflowRun.workflow_prompt);
                          const hasFormattedHistory = selectedWorkflowRun.workflow_prompt.includes('formatted_history');
                          return (
                            <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="text-xs font-medium text-slate-700">Workflow Prompt:</div>
                                {hasFormattedHistory && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-200">
                                    From Thread
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-900 whitespace-pre-wrap">
                                {parsed.userPrompt}
                              </p>
                              {parsed.hasThreadHistory && parsed.threadHistory.length > 0 && (
                                <CollapsibleThreadHistory history={parsed.threadHistory} />
                              )}
                              {hasFormattedHistory && parsed.threadHistory.length === 0 && parsed.mainQuery && (
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                  <div className="text-[10px] text-slate-500 italic">
                                    This workflow was created from a thread with context: "{parsed.mainQuery.substring(0, 100)}{parsed.mainQuery.length > 100 ? '...' : ''}"
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Brand Map Display */}
                        {selectedWorkflowRun.workflow_brand_map && Object.keys(selectedWorkflowRun.workflow_brand_map).length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Building className="h-3 w-3 text-blue-600" />
                            <span className="text-xs font-medium">Brands:</span>
                            {Object.entries(selectedWorkflowRun.workflow_brand_map).slice(0, 3).map(([brandId, brandName]) => (
                              <Badge key={brandId} variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {String(brandName) || `Brand ${brandId}`}
                              </Badge>
                            ))}
                            {Object.keys(selectedWorkflowRun.workflow_brand_map).length > 3 && (
                              <span className="text-xs text-muted-foreground">
                                +{Object.keys(selectedWorkflowRun.workflow_brand_map).length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Session Info */}
                        <div className="text-xs text-muted-foreground/70 truncate">
                          <span className="font-medium">Session ID:</span> {selectedWorkflowRun.session_id}
                        </div>
                      </div>
                    </div>

                    {/* Chat Messages for Workflow Run */}
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <div className="h-full admin-scroll overflow-y-auto p-3 pb-6 space-y-3">
                        {loadingWorkflowChat ? (
                          <div className="flex items-center justify-center py-8">
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            <span className="ml-2 text-sm">Loading workflow response...</span>
                          </div>
                        ) : workflowChatMessages && workflowChatMessages.length > 0 ? (
                          <>
                            {workflowChatMessages.map((message: ChatMessage, index: number) => {
                              // Parse the message content for user messages
                              const parsedContent = message.role === "user" 
                                ? parseWorkflowPrompt(message.content) 
                                : null;
                              
                              return (
                                <Message key={index} className="gap-2">
                                  <MessageAvatar
                                    src=""
                                    alt={message.role}
                                    fallback={message.role === "user" ? "W" : "A"}
                                    className={cn(
                                      "h-6 w-6 flex-shrink-0",
                                      message.role === "user"
                                        ? "bg-slate-100 text-slate-700"
                                        : "bg-slate-200 text-slate-800"
                                    )}
                                  />
                                  <div className="flex-1 space-y-1 min-w-0">
                                    {message.role === "user" ? (
                                      <div className="bg-slate-50 rounded-lg p-3 text-sm border border-slate-200 max-w-full overflow-x-auto">
                                        <div className="text-xs font-medium text-slate-600 mb-2">Workflow Input</div>
                                        <pre className="whitespace-pre-wrap font-sans text-slate-900 break-words overflow-wrap-anywhere max-w-full">
                                          {parsedContent?.userPrompt || message.content}
                                        </pre>
                                      </div>
                                    ) : (
                                      <div className="max-w-full overflow-x-auto">
                                        <div className="text-xs font-medium text-slate-600 mb-1">Workflow Response</div>
                                        <MessageContent
                                          markdown
                                          className="rounded-lg p-2 text-sm bg-slate-100 border prose prose-xs dark:prose-invert max-w-none prose-pre:whitespace-pre-wrap prose-p:whitespace-pre-line text-slate-900 break-words overflow-wrap-anywhere prose-table:text-xs prose-table:w-full prose-table:table-auto prose-th:break-words prose-td:break-words prose-th:max-w-0 prose-td:max-w-0 prose-th:overflow-wrap-anywhere prose-td:overflow-wrap-anywhere"
                                        >
                                          {message.content}
                                        </MessageContent>
                                      </div>
                                    )}
                                    
                                    {/* Message metadata */}
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                      {message.hasCode && (
                                        <div className="flex items-center gap-1">
                                          <Code className="h-3 w-3" />
                                          <span>Code</span>
                                        </div>
                                      )}
                                      {message.timestamp && (
                                        <div className="flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          <span>
                                            {format(new Date(message.timestamp), "HH:mm")}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Message>
                              );
                            })}
                            <div className="h-4" aria-hidden="true" />
                          </>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">
                              No messages found for this workflow run
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent
              value="master-memories"
              className="flex-1 min-h-0 overflow-hidden m-0"
            >
              <MasterMemoriesPanel />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Resizable Divider */}
      {selectedSessionId && (
        <div
          ref={dividerRef}
          className="w-4 cursor-col-resize flex items-center justify-center bg-border hover:bg-gray-300 transition-all duration-150 group relative"
          onMouseDown={handleMouseDown}
        >
          {/* Visual handle with dots pattern */}
          <div className="flex flex-col items-center justify-center gap-0.5 h-20">
            {/* Three horizontal lines pattern (classic resize handle) */}
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors" />
            <div className="w-1 h-3 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors" />
            <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors" />
          </div>
          
          {/* Hover indicator */}
          <div className="absolute inset-0 bg-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-150 rounded-sm" />
        </div>
      )}

      {/* Chat History Panel - Resizable */}
      {selectedSessionId && (
        <div
          className="flex flex-col bg-background border-l min-h-0 max-h-screen"
          style={{
            width: `${(1 - panelRatio) * 100}%`,
            transition: isResizing ? "none" : "width 0.2s ease",
          }}
        >
          {/* Chat Header */}
          <div className="border-b bg-card shadow-sm flex-shrink-0 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                <h2 className="text-sm font-semibold">Chat History</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToSessions}
                className="h-7 px-2"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            
            {/* Enhanced Session Information */}
            <div className="mt-2 space-y-2">
              {/* Session Name and ID */}
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-foreground max-w-[550px] whitespace-normal break-words" title={selectedSessionDetails?.session_name || selectedSessionId}>
                  {selectedSessionDetails?.session_name || selectedSessionId}
                </div>
               
                {/* Agency Mode Badge */}
                {sessionMetadata?.agencyMode && (
                  <Badge variant="secondary" className="h-4 text-xs bg-purple-100 text-purple-800 border-purple-300">
                    Agency Mode
                  </Badge>
                )}
              </div>
              
              {/* Session Metadata Row 1 */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                {/* User Information */}
                {selectedSessionDetails?.user_id && (
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span className="font-medium">User:</span>
                    <span className="truncate max-w-[120px]">
                      {userMap?.[selectedSessionDetails.user_id] || selectedSessionDetails.user_id}
                    </span>
                  </div>
                )}
                
                {/* Brand Information from session metadata */}
                {sessionMetadata?.brandMap && formatBrandNames(sessionMetadata.brandMap) && (
                  <div className="flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    <span className="font-medium">Brands:</span>
                    <span className="truncate max-w-[200px]" title={formatBrandNames(sessionMetadata.brandMap) || undefined}>
                      {formatBrandNames(sessionMetadata.brandMap)}
                    </span>
                  </div>
                )}
                
                {/* Fallback: Brand Information from session data */}
                {!sessionMetadata?.brandMap && (selectedSessionDetails as any)?.brand_id && (
                  <div className="flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    <span className="font-medium">Brand:</span>
                    <span className="truncate max-w-[120px]">
                      {brandMap?.[(selectedSessionDetails as any).brand_id] || `Brand ${(selectedSessionDetails as any).brand_id}`}
                    </span>
                  </div>
                )}
                
                {/* Message Count */}
                {selectedSessionDetails?.chat_length && (
                  <div className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    <span className="font-medium">Messages:</span>
                    <span>{selectedSessionDetails.chat_length}</span>
                  </div>
                )}

                {/* Model Information from metadata */}
                {sessionMetadata?.model && (
                  <div className="flex items-center gap-1">
                    <Settings className="h-3 w-3" />
                    <span className="font-medium">Model:</span>
                    <span className="truncate max-w-[100px]">
                      {sessionMetadata.model}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Session Metadata Row 2 - Timestamps */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                {/* Created Date */}
                {selectedSessionDetails?.created_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span className="font-medium">Created:</span>
                    <span>
                      {format(new Date(selectedSessionDetails.created_at), "MMM dd, yyyy HH:mm")}
                    </span>
                  </div>
                )}
                
                {/* Last Updated */}
                {selectedSessionDetails?.last_updated && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span className="font-medium">Updated:</span>
                    <span>
                      {format(new Date(selectedSessionDetails.last_updated), "MMM dd, yyyy HH:mm")}
                    </span>
                  </div>
                )}

                {/* Loading indicator for metadata */}
                {loadingMetadata && (
                  <div className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Loading metadata...</span>
                  </div>
                )}
              </div>
              
              {/* Session ID for reference */}
              <div className="text-xs text-muted-foreground/70 truncate">
                <span className="font-medium">ID:</span> {selectedSessionId}
              </div>
              <div className="flex items-center gap-2">
              {/* User Memories Button */}
              {selectedSessionDetails?.user_id && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => selectedSessionDetails.user_id && handleViewUserMemories(selectedSessionDetails.user_id)}
                    className="gap-1 h-7 px-2 text-xs bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100"
                  >
                    <Brain className="h-3 w-3" />
                    View User Memories
                  </Button>
                </div>
              )}

{selectedSessionDetails?.is_active && (
                  <Badge variant="default" className="mt-2 h-7 px-2 py-0 text-xs bg-green-100 text-green-800 border-green-300">
                    Active
                  </Badge>
                )}  
                </div>
            </div>
          </div>

          {/* Chat Messages - Fixed viewport constraints */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="h-full admin-scroll overflow-y-auto p-3 pb-6 space-y-3">
              {loadingChat ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm">Loading chat...</span>
                </div>
              ) : chatMessages && chatMessages.length > 0 ? (
                <>
                  {chatMessages.map((message: ChatMessage, index: number) => {
                    return (
                      <Message key={index} className="gap-2">
                        <MessageAvatar
                          src=""
                          alt={message.role}
                          fallback={message.role === "user" ? "U" : "A"}
                          className={cn(
                            "h-6 w-6 flex-shrink-0",
                            message.role === "user"
                              ? "bg-slate-100 text-slate-700"
                              : "bg-slate-200 text-slate-800"
                          )}
                        />
                        <div className="flex-1 space-y-1 min-w-0">
                          {message.role === "user" ? (
                            <div className="bg-slate-50 rounded-lg p-2 text-sm border max-w-full overflow-x-auto">
                              <pre className="whitespace-pre-wrap font-sans text-slate-900 break-words overflow-wrap-anywhere max-w-full">
                                {message.content}
                              </pre>
                            </div>
                          ) : (
                            <div className="max-w-full overflow-x-auto">
                              <MessageContent
                                markdown
                                className="rounded-lg p-2 text-sm bg-slate-100 border prose prose-xs dark:prose-invert max-w-none prose-pre:whitespace-pre-wrap prose-p:whitespace-pre-line text-slate-900 break-words overflow-wrap-anywhere prose-table:text-xs prose-table:w-full prose-table:table-auto prose-th:break-words prose-td:break-words prose-th:max-w-0 prose-td:max-w-0 prose-th:overflow-wrap-anywhere prose-td:overflow-wrap-anywhere"
                              >
                                {message.content}
                              </MessageContent>

                              {/* Show More/Less button for truncated content */}
                              {message.isTruncated && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    // Toggle between truncated and full content
                                    const updatedMessages = chatMessages.map(
                                      (msg, idx) => {
                                        if (idx === index) {
                                          return {
                                            ...msg,
                                            content:
                                              msg.content === message.rawContent
                                                ? message.rawContent?.substring(
                                                    0,
                                                    1000
                                                  ) + "..."
                                                : message.rawContent ||
                                                  msg.content,
                                          };
                                        }
                                        return msg;
                                      }
                                    );
                                    setChatMessages(updatedMessages);
                                  }}
                                  className="mt-2 h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                >
                                  {message.content === message.rawContent
                                    ? "Show Less"
                                    : "Show More"}
                                </Button>
                              )}
                            </div>
                          )}

                          {/* HTML Visualization Button */}
                          {message.htmlSnippets &&
                            message.htmlSnippets.length > 0 && (
                              <HtmlVisualization
                                htmlSnippets={message.htmlSnippets}
                              />
                            )}

                          {/* Message metadata */}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                            {message.hasCode && (
                              <div className="flex items-center gap-1">
                                <Code className="h-3 w-3" />
                                <span>Code</span>
                              </div>
                            )}
                            {message.timestamp && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {format(new Date(message.timestamp), "HH:mm")}
                                </span>
                              </div>
                            )}
                            
                            {/* Enhanced feedback indicators with run_id */}
                            {message.role === "assistant" && message.run_id && (
                              <div className="flex items-center gap-1 text-xs">
                                <span className="text-gray-400">ID:</span>
                                <span className="font-mono text-gray-600">{message.run_id.substring(0, 8)}...</span>
                              </div>
                            )}
                            
                            {/* Enhanced feedback status indicators */}
                            {message.feedback?.type && message.feedback.type !== 'unknown' && (
                              <div className="flex items-center gap-1">
                                {message.feedback.type === 'liked' && (
                                  <>
                                    <ThumbsUp className="h-3 w-3 text-emerald-500" />
                                    <span className="text-emerald-600 text-xs font-medium">Liked</span>
                                  </>
                                )}
                                {message.feedback.type === 'disliked' && (
                                  <>
                                    <ThumbsDown className="h-3 w-3 text-rose-500" />
                                    <span className="text-rose-600 text-xs font-medium">Disliked</span>
                                  </>
                                )}
                                {message.feedback.type === 'no_feedback' && (
                                  <span className="text-gray-500 text-xs">No feedback</span>
                                )}
                              </div>
                            )}
                            
                            {/* Show comment and reasons if available */}
                            {(message.feedback?.comment || message.feedback?.reasons) && (
                              <div className="flex items-start gap-1 max-w-lg flex-col">
                                {message.feedback.comment && message.feedback.comment.trim() && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400">Comment:</span>
                                    <span className="text-gray-600 text-xs truncate" title={message.feedback.comment}>
                                      "{message.feedback.comment}"
                                    </span>
                                  </div>
                                )}
                                {message.feedback.reasons && message.feedback.reasons.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400">Reasons:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {message.feedback.reasons.map((reason, idx) => (
                                        <Badge
                                          key={idx}
                                          variant="secondary"
                                          className="h-4 text-xs bg-orange-100 text-orange-700 border-orange-200"
                                        >
                                          {reason.replace(/_/g, ' ')}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </Message>
                    );
                  })}
                  {/* Bottom spacer to ensure last message is fully visible */}
                  <div className="h-4" aria-hidden="true" />
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    No messages found in this conversation
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* HTML Report Dialog */}
      <Dialog open={showHtmlDialog} onOpenChange={setShowHtmlDialog}>
        <DialogPortal>
          {/* Custom backdrop with blur instead of dark overlay */}
          <div
            className="fixed inset-0 backdrop-blur-md z-[9997]"
            onClick={() => setShowHtmlDialog(false)}
          />
          {/* Custom DialogContent without default overlay */}
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[9999] translate-x-[-50%] translate-y-[-50%] max-w-7xl w-[95vw] max-h-[95vh] h-[90vh] p-2 bg-transparent border-0 shadow-none focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <DialogHeader className="sr-only">
              <DialogTitle>LIFT AI Report Viewer</DialogTitle>
            </DialogHeader>
            <HTMLDialogView
              html={selectedHtml}
              onClose={() => setShowHtmlDialog(false)}
            />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* User Memories Dialog */}
      <Dialog open={showUserMemoriesDialog} onOpenChange={setShowUserMemoriesDialog}>
        <DialogPortal>
          {/* Blurred background instead of dark shadow */}
          <div className="fixed inset-0 z-[9997] backdrop-blur-md pointer-events-none" />
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[9999] translate-x-[-50%] translate-y-[-50%] bg-white border border-gray-200 shadow-2xl rounded-lg p-0 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div
              className={`transition-all duration-200 ease-in-out overflow-hidden ${loadingUserMemories
                ? 'w-[350px] h-[120px] p-2'
                : 'w-[700px] max-w-4xl max-h-[80vh] p-8'} bg-white rounded-lg`}
            >
              {/* Close button */}
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>

              <DialogHeader className="border-b border-gray-100 pb-4 mb-4">
                <DialogTitle className="flex items-center gap-2 text-gray-900">
                  <Brain className="h-5 w-5 text-purple-600" />
                  User Memories
                  {selectedUserId && (
                    <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-800 border-purple-200">
                      User: {userMap?.[selectedUserId] || selectedUserId}
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="flex-1 overflow-hidden bg-white">
                {loadingUserMemories ? (
                  <div className="flex items-center justify-center py-8 bg-white">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                    <span className="ml-2 text-sm text-gray-700">Loading user memories...</span>
                  </div>
                ) : userMemories.length > 0 ? (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto admin-scroll bg-white p-1">
                    {userMemories.map((memory, index) => (
                      <Card key={memory.memory_id} className="border border-purple-200 bg-purple-50/50 shadow-sm">
                        <CardContent className="p-4 bg-white/90">
                          <div className="space-y-3">
                            {/* Memory Content */}
                            <div className="bg-white rounded-lg p-3 border border-purple-100 shadow-sm">
                              <h4 className="text-sm font-semibold text-purple-800 mb-2">Memory</h4>
                              <p className="text-sm text-gray-800 leading-relaxed">{memory.memory}</p>
                            </div>
                            
                            {/* Original Input */}
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 shadow-sm">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Original Input</h4>
                              <p className="text-sm text-gray-600 italic">"{memory.input}"</p>
                            </div>
                            
                            {/* Metadata */}
                            <div className="flex items-center justify-between text-xs text-muted-foreground bg-white/80 rounded p-2">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                  <Hash className="h-3 w-3" />
                                  <span className="font-mono">{memory.memory_id.substring(0, 8)}...</span>
                                </div>
                                {memory.topics && memory.topics.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium">Topics:</span>
                                    <span>{memory.topics.join(', ')}</span>
                                  </div>
                                )}
                              </div>
                              {memory.last_updated && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  <span>{format(new Date(memory.last_updated), "MMM dd, yyyy HH:mm")}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground bg-white">
                    <Brain className="h-12 w-12 mx-auto mb-4 opacity-50 text-purple-400" />
                    <h3 className="text-lg font-medium mb-2 text-gray-800">No Memories Found</h3>
                    <p className="text-sm text-gray-600">
                      This user doesn't have any stored memories yet.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Footer with summary */}
              {userMemories.length > 0 && (
                <div className="border-t border-gray-100 pt-4 mt-4 bg-white">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span className="text-gray-700">
                      <strong>{userMemories.length}</strong> {userMemories.length === 1 ? 'memory' : 'memories'} found
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowUserMemoriesDialog(false)}
                      className="gap-1 bg-white hover:bg-gray-50 border-gray-300"
                    >
                      <X className="h-3 w-3" />
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
